import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk } from "../../store";
import { TMSResponseProp, RequestTmsParams } from "./types";
import { exportTmsApi, getTmsListApi } from "./api";

type LiteTmsType = TMSResponseProp & {
  loading: boolean;
  error: string | null;
  success: string | null;
  currentParams: Partial<RequestTmsParams> | null;
  exporting: {
    loading: boolean;
    error: string | null;
  };
};

const initialState: LiteTmsType = {
  items: null,
  loading: false,
  error: null,
  success: null,
  total: 0,
  page: 0,
  size: 0,
  currentParams: null,
  exporting: {
    loading: false,
    error: null,
  },
};

const liteWms = createSlice({
  name: "liteTms",
  initialState: initialState,
  reducers: {
    getTmsStart(state: LiteTmsType) {
      state.loading = true;
      state.error = null;
    },
    getTmsSuccess(
      state: LiteTmsType,
      action: PayloadAction<TMSResponseProp & { params?: RequestTmsParams }>,
    ) {
      const { items, total, page, size, params } = action.payload;
      state.items = items;
      state.total = total;
      state.page = page;
      state.size = size;
      state.loading = false;
      if (params) {
        const { page, size, ...exportParams } = params;
        state.currentParams = exportParams;
      }
    },
    getTmsFailure(state: LiteTmsType, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
      state.items = null;
    },
    getExportFailure(state: LiteTmsType, action: PayloadAction<string>) {
      state.error = action.payload;
    },
    hideWarning(state: LiteTmsType) {
      state.error = null;
    },
    cleanPackage(state: LiteTmsType) {
      state.items = null;
    },
    startExport(state: LiteTmsType) {
      state.exporting = {
        loading: true,
        error: null,
      };
    },
    successExport(state: LiteTmsType) {
      state.exporting.loading = false;
      state.exporting.error = null;
    },
  },
});

export const {
  getTmsStart,
  hideWarning,
  cleanPackage,
  getTmsSuccess,
  getTmsFailure,
  getExportFailure,
  startExport,
  successExport,
} = liteWms.actions;

export default liteWms.reducer;

export const fetchTmsList =
  (params: RequestTmsParams): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getTmsStart());
      const data = await getTmsListApi(params);
      dispatch(getTmsSuccess({ ...data, params }));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getTmsFailure(error));
    }
  };

export const exportTms =
  (params?: Partial<RequestTmsParams>): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(startExport());
      const { file, filename } = await exportTmsApi(params);

      const url = window.URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(successExport());
    } catch {
      dispatch(getTmsFailure("Downloading failed. Try again later."));
    }
  };
